import type { FlaggedGroupsResponse, Group, MeResponse, ResolveFlagRequest } from '@stalvi/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GroupMembersGrid } from '../components/GroupMembers';
import { GroupsNav } from '../components/GroupsNav';
import { Header } from '../components/Header';
import { useToast } from '../components/Toast';
import { ApiError, api } from '../lib/api';

function FlaggedCard({
  group,
  onResolve,
  busy,
}: {
  group: Group;
  onResolve: (action: ResolveFlagRequest['action']) => void;
  busy: boolean;
}) {
  return (
    <section className="card p-4 flex flex-col gap-3" data-testid="flagged-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="font-medium">Grupo {group.groupId}</p>
          <p className="text-xs text-text-tertiary">
            {group.provenance} · {group.members.length} miembros
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy}
            onClick={() => onResolve('keep')}
          >
            Mantener
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy}
            onClick={() => onResolve('dissolve')}
          >
            Disolver
          </button>
        </div>
      </div>
      <GroupMembersGrid members={group.members} />
    </section>
  );
}

export function GroupsFlagged({ me, onSignOut }: { me: MeResponse; onSignOut: () => void }) {
  const qc = useQueryClient();
  const { show } = useToast();

  const { data, isLoading, error } = useQuery<FlaggedGroupsResponse>({
    queryKey: ['flagged-groups'],
    queryFn: () => api.flaggedGroups(),
  });

  const resolve = useMutation({
    mutationFn: ({ groupId, action }: { groupId: string; action: ResolveFlagRequest['action'] }) =>
      api.resolveFlag(groupId, action),
    onSuccess: (_res, { action }) => {
      show(action === 'dissolve' ? 'Grupo disuelto' : 'Grupo mantenido', 'info');
      void qc.invalidateQueries({ queryKey: ['flagged-groups'] });
    },
    onError: (e) => show(e instanceof ApiError ? e.apiMessage : 'Hubo un problema', 'error'),
  });

  const items = data?.items ?? [];

  return (
    <div className="stalvi-app flex flex-col">
      <Header me={me} onSignOut={onSignOut} />
      <GroupsNav />
      <main className="flex-1 px-6 py-6 overflow-auto">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-xl font-semibold mb-4">Grupos marcados</h1>

          {error && (
            <p role="alert" className="text-danger mb-4">
              Hubo un problema, intenta de nuevo.
            </p>
          )}

          {isLoading ? (
            <div className="card p-8">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="card p-8 text-center text-text-secondary">No hay grupos marcados.</div>
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((g) => (
                <FlaggedCard
                  key={g.groupId}
                  group={g}
                  busy={resolve.isPending}
                  onResolve={(action) => resolve.mutate({ groupId: g.groupId, action })}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
